import { Box } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { isTextHebrew } from '@wix/wix-chatbot-common/locale';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { hasContent } from '../../../../util/guards';
import { IUserMessage } from '../../../common/messageCreators';
import { selectUndelivered } from '../../../common/selectors/chat';
import { isHtmlHebrew } from '../../../util/language';
import { navigationControl } from '../../../util/navigationControl';
import { useResend } from '../../hooks/userActions';
import { BotMessageIcon } from '../BotMessage/BotMessageIcon';
import { ExtraChatMessageProps } from '../Chat';
import { DirectionWrapper } from '../DirectionWrapper';
import { MessageBubble } from '../MessageBubble';
import { MessageItem } from '../MessageItem';
import { ChangeButton } from './ChangeButton';
import s from './styles.scss';

export const UserMessage: FC<IUserMessage & ExtraChatMessageProps> = ({
  id,
  text,
  html,
  changeId,
  disabled,
  isLiveChat,
  isFirstInGroup,
}) => {
  const biMessageText = hasContent(html) ? sanitizeHtml(html) : text;
  const [needResend] = useResend(id, biMessageText);
  const dispatch = useDispatch();
  const isUndelivered = Boolean(useSelector(selectUndelivered(id)));

  useEffect(() => {
    if (!navigationControl.isLocked) {
      navigationControl.lock();
    }
  }, [dispatch]);

  const hasChangeButton = !isLiveChat && !needResend && hasContent(changeId);

  const changeButton = hasChangeButton && <ChangeButton id={id} nodeId={changeId} disabled={disabled} />;

  const isHebrew = useMemo(() => {
    return isTextHebrew(text) || isHtmlHebrew(html);
  }, [text, html]);

  const message = hasContent(html) ? (
    <span id={id} data-hook="message" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
  ) : (
    <span id={id} data-hook="message">
      {text}
    </span>
  );

  return (
    <MessageItem id={id} fromUser width="full">
      <Box direction="vertical" gap="SP1" align="right">
        <Box direction="horizontal" gap="1" verticalAlign="middle">
          {isUndelivered && <BotMessageIcon icon="warning" />}
          <MessageBubble fromUser isFirstInGroup={isFirstInGroup}>
            <DirectionWrapper isRtl={isHebrew}>
              <Box direction="vertical" whiteSpace="pre-wrap">
                <div className={s.msg}>{message}</div>
              </Box>
            </DirectionWrapper>
          </MessageBubble>
        </Box>
        {changeButton}
      </Box>
    </MessageItem>
  );
};
